import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  ReviewServices,
  ContentPageServices,
  CreateContentPageFromReview,
  ReviewReplyStatus,
} from "@/services/bedrock";
import { resolveReviewDatePreset } from "@/components/reviews";
import { useSocialCoachSession } from "@/utils";
import { useCallback } from "react";
import { toast } from "sonner";

export function useReviewsState() {
  const { playerId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  // 1. Query for the total count of unreplied reviews in last 30 days (shares cache across all mounts)
  const { data: countData, isLoading: isLoadingCount } = useQuery({
    queryKey: ["reviews-unreplied-count", playerId],
    queryFn: () => {
      const { startDate, endDate } = resolveReviewDatePreset("last_30_days");
      return ReviewServices.getReviews({
        playerId: playerId || undefined,
        replied: false,
        replySkipped: false,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
        perPage: 1,
      });
    },
    enabled: !!playerId,
  });

  const unrepliedCount = countData?.pagination?.totalCount ?? 0;

  // 2. Invalidate all review queries
  const invalidateReviews = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["reviews-unreplied-count"] });
    queryClient.invalidateQueries({ queryKey: ["reviews-inbox-list-infinite"] });
    queryClient.invalidateQueries({ queryKey: ["review-details"] });
  }, [queryClient]);

  // 3. Mutation to update a review's status (e.g. mark as replied/skipped)
  const updateReviewMutation = useMutation({
    mutationFn: async ({ reviewId, replyStatus }: { reviewId: string | number; replyStatus: ReviewReplyStatus }) => {
      return ReviewServices.updateReview(reviewId, { replyStatus });
    },
    onSuccess: () => {
      toast.success("Review status updated successfully!");
      invalidateReviews();
    },
    onError: error => {
      console.error("Error updating review:", error);
      toast.error("Failed to update review. Please try again.");
    },
  });

  // 4. Mutation to create a content page from a review
  const createPageMutation = useMutation({
    mutationFn: async (request: CreateContentPageFromReview) => {
      return ContentPageServices.createContentPageFromReview(request);
    },
    onSuccess: () => {
      toast.success("Response video page created successfully!");
      invalidateReviews();
    },
    onError: error => {
      console.error("Error creating video page:", error);
      toast.error("Failed to create response video page.");
    },
  });

  return {
    unrepliedCount,
    isLoadingCount,
    invalidateReviews,
    updateReview: updateReviewMutation.mutateAsync,
    isUpdatingReview: updateReviewMutation.isPending,
    createContentPage: createPageMutation.mutateAsync,
    isCreatingPage: createPageMutation.isPending,
  };
}
